import { Entity } from '@api';
import { Accordion, Icon, SkeletonLoader } from '@design';
import { RangeHorizon } from '@helpers';
import { RowSelectionState } from '@tanstack/react-table';
import { PriceMovementChart } from '@terminal/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { useScenarios } from '@terminal/react-query-hooks/scenarios/useScenarios';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getMarketHoursLabel } from '../../utils/utils';
import styles from './EarningsPriceMovementsChartAccordion.module.scss';

const HORIZONS = [
  RangeHorizon.OneDay,
  RangeHorizon.TwoDays,
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
];

export interface EarningsPriceMovementsChartAccordionProps {
  rowSelection: RowSelectionState;
  entity: Entity;
}

export const EarningsPriceMovementsChartAccordion = ({
  rowSelection,
  entity,
}: EarningsPriceMovementsChartAccordionProps) => {
  const { t } = useTranslation('widget');
  const selectedRowsLength = Object.values(rowSelection).filter(Boolean).length;

  const { error, isLoading, data, sendMessage } = useScenarios();
  const scenario = data[0];

  useEffect(() => {
    if (selectedRowsLength) {
      const reportingDates = Object.keys(rowSelection).filter(
        key => rowSelection[key]
      );
      const body = {
        entities: [
          {
            tag: entity.tag,
            snake: entity.default_snake,
          },
        ],
        events: reportingDates.map(reportingDate => ({
          date: reportingDate,
          market_hours: getMarketHoursLabel(new Date(reportingDate)),
        })),
        horizons: HORIZONS,
        start_horizon: '0d',
        end_horizon: '1m',
        skip_report: true,
      };
      sendMessage(body);
    }
  }, [rowSelection, entity, sendMessage]);

  const renderAccordionContent = () => {
    if (isLoading) {
      return (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '293px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      );
    }

    if (error) {
      throw new Error(
        'EarningsPriceMovementsChartAccordion: useScenarios error'
      );
    }

    if (!scenario || !selectedRowsLength) {
      return null;
    }

    const prediction = scenario.predictions.map(p => ({
      high: p.high,
      median: p.median,
      low: p.low,
      relative_idx: p.relative_idx,
    }));

    return (
      <PriceMovementChart
        prediction={prediction}
        defaultHorizonToIndex={
          new Map<string, number>([
            ['1d', 1],
            ['2d', 2],
            ['1w', 4],
            ['2w', 9],
            ['1m', 20],
          ])
        }
        defaultHorizons={HORIZONS}
        yAxisLabel={t('widget:earnings.return%')}
        useDynamicDaysLabel
      />
    );
  };

  return (
    <div className={styles.chartOverrides}>
      <Accordion
        className={styles.styledAccordion}
        data-testid="earnings-price-movements-chart-accordion"
        items={[
          {
            id: 'scenario',
            title: (
              <span className={styles.styledTextDefaultSpan}>
                {t('widget:earnings.earningsScenario.priceMovementsFollowing')}
                <span className={styles.styledTextPrimarySpan}>
                  {` ${selectedRowsLength} `}
                </span>
                <span className={styles.styledTextDefaultSpan}>
                  {t(
                    'widget:earnings.earningsScenario.selectedEarningsReleases',
                    {
                      count: selectedRowsLength,
                    }
                  )}
                </span>
              </span>
            ),
            content: renderAccordionContent(),
            renderIcon: (expanded: boolean) => (
              <Icon
                className={clsx(
                  expanded ? styles.iconExpanded : styles.iconCustomAnimation
                )}
                iconName="ChevronLightRight"
                fillColor="var(--icon-default)"
                size={20}
              />
            ),
          },
        ]}
      />
    </div>
  );
};
